import { createAsyncThunk } from '@reduxjs/toolkit';

import { buscarBytesRemotos, salvarBlobComoArquivo } from '@/shared/utils/browserActions';
import { getErrorMessage } from '@/shared/utils/error';
import type { RootState } from '@/store';

import type { ResultadoDownload } from '../types/autenticacaoPublica.types';
import { registrarCadeiaNaoConfirmada } from '../utils/registroDaCadeia';

import { selectIsLoadingDownload } from './selectors';
import { autenticacaoPublicaService, RateLimitException } from './services';

const nomeDoArquivoBaixado = (codigo: string) => `documento-${codigo.toLowerCase()}.pdf`;

export const consultarDocumento = createAsyncThunk(
  'autenticacaoPublica/consultar',
  async (codigo: string, { rejectWithValue }) => {
    try {
      const resultado = await autenticacaoPublicaService.consultarCodigo(codigo);
      registrarCadeiaNaoConfirmada('codigo', resultado);
      return resultado;
    } catch (error) {
      if (error instanceof RateLimitException) {
        return rejectWithValue({
          message: error.message,
          retryAfterSeconds: error.retryAfterSeconds,
        });
      }
      return rejectWithValue({ message: getErrorMessage(error) });
    }
  },
);

export const verificarArquivo = createAsyncThunk(
  'autenticacaoPublica/verificarArquivo',
  async (file: File, { rejectWithValue }) => {
    try {
      const resultado = await autenticacaoPublicaService.verificarArquivo(file);
      registrarCadeiaNaoConfirmada('arquivo', resultado);
      return resultado;
    } catch (error) {
      if (error instanceof RateLimitException) {
        return rejectWithValue({
          message: error.message,
          retryAfterSeconds: error.retryAfterSeconds,
        });
      }
      return rejectWithValue({ message: getErrorMessage(error) });
    }
  },
);

/**
 * Baixa o documento autenticado: pede a URL à rota pública e **salva os bytes**.
 *
 * O download vive aqui, e não no hook, porque assim a falha de rede do storage cai no
 * `rejected` da fatia e chega à tela. Na versão anterior o hook fazia
 * `window.open(redirectUrl, '_blank')` DEPOIS do `await`: fora da task do gesto o bloqueador de
 * pop-up engolia a janela, e qualquer falha posterior morria num `catch` vazio.
 *
 * Buscar os bytes (`credentials: 'omit'`) em vez de navegar para a URL assinada também é
 * deliberado: ela sai do GCS sem `Content-Disposition: attachment`, então navegar abriria o
 * visualizador de PDF por cima da tela pública em vez de entregar o arquivo — e a tela existe
 * para que um terceiro guarde a peça, não para folheá-la.
 */
export const downloadDocumento = createAsyncThunk<
  ResultadoDownload,
  string,
  { rejectValue: { message: string; retryAfterSeconds?: number }; state: RootState }
>(
  'autenticacaoPublica/download',
  async (codigo: string, { rejectWithValue, signal }) => {
    try {
      const resposta = await autenticacaoPublicaService.downloadDocumento(codigo, signal);
      if ('redirectUrl' in resposta) {
        const bytes = await buscarBytesRemotos(resposta.redirectUrl, signal);
        // O abort do RTK só descarta o RESULTADO do thunk — o corpo continua rodando. Sem esta
        // checagem, a sessão expirada (ou a tela fechada) ainda gravava o arquivo no disco.
        signal.throwIfAborted();
        salvarBlobComoArquivo(bytes, nomeDoArquivoBaixado(codigo));
        return { status: 'SAVED' };
      }
      return { status: 'GENERATING' };
    } catch (error) {
      if (error instanceof RateLimitException) {
        return rejectWithValue({
          message: error.message,
          retryAfterSeconds: error.retryAfterSeconds,
        });
      }
      return rejectWithValue({ message: getErrorMessage(error) });
    }
  },
  {
    // Um clique é uma requisição. Sem esta trava o duplo clique — ou um tick do polling caindo
    // sobre uma resposta ainda em voo — multiplicaria requisições contra uma rota com teto por
    // IP, e o usuário pagaria com 429 pelo próprio zelo.
    condition: (_codigo, { getState }) => !selectIsLoadingDownload(getState()),
  },
);
